import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { Comment } from '@/types/comment.types';


export const usePostComments = (postId: string) => {
  const [showComments, setShowComments] = useState(true);
  const [newComment, setNewComment] = useState('');
  const [replyingTo, setReplyingTo] = useState<string | null>(null);
  const [replyContent, setReplyContent] = useState('');
  const queryClient = useQueryClient();
  

  // Fetch comments with replies
  const { data: comments = [], isLoading } = useQuery({
    queryKey: ['comments', postId],
    queryFn: async () => {
      // Get current user for like status
      const { data: { user } } = await supabase.auth.getUser();

      // Buscar comentários principais (sem parent_comment_id)
      const { data: mainComments, error: mainError } = await supabase
        .from('community_post_comments')
        .select(`
          id,
          content,
          created_at,
          updated_at,
          post_id,
          parent_comment_id,
          likes_count,
          image_url,
          gif_url,
          public_profiles!inner(
            id,
            full_name,
            logo_url
          )
        `)
        .eq('post_id', postId)
        .is('parent_comment_id', null)
        .is('deleted_at', null)
        .order('created_at', { ascending: true });

      if (mainError) throw mainError;

      // Get all comment IDs to check likes
      const allCommentIds = (mainComments || []).map((c: any) => c.id);

      // Get user likes for all comments
      let userLikes: Set<string> = new Set();
      if (user) {
        const { data: likesData } = await supabase
          .from('community_comment_likes')
          .select('comment_id')
          .eq('user_id', user.id)
          .in('comment_id', allCommentIds);
        
        userLikes = new Set((likesData || []).map((l: any) => l.comment_id));
      }

      // Para cada comentário, buscar respostas
      const commentsWithReplies = await Promise.all(
        (mainComments || []).map(async (comment: any) => {
          const { data: replies, error: repliesError } = await supabase
            .from('community_post_comments')
            .select(`
              id,
              content,
              created_at,
              updated_at,
              post_id,
              parent_comment_id,
              likes_count,
              image_url,
              gif_url,
              public_profiles!inner(
                id,
                full_name,
                logo_url
              )
            `)
            .eq('parent_comment_id', comment.id)
            .is('deleted_at', null)
            .order('created_at', { ascending: true });

          if (repliesError) console.error('Error fetching replies:', repliesError);

          // Get reply IDs for likes check
          const replyIds = (replies || []).map((r: any) => r.id);
          let replyLikes: Set<string> = new Set();
          if (user && replyIds.length > 0) {
            const { data: replyLikesData } = await supabase
              .from('community_comment_likes')
              .select('comment_id')
              .eq('user_id', user.id)
              .in('comment_id', replyIds);
            
            replyLikes = new Set((replyLikesData || []).map((l: any) => l.comment_id));
          }

          const formattedReplies = (replies || []).map((reply: any) => ({
            id: reply.id,
            content: reply.content,
            created_at: reply.created_at,
            updated_at: reply.updated_at,
            post_id: reply.post_id,
            parent_comment_id: reply.parent_comment_id,
            likes_count: reply.likes_count || 0,
            is_liked: replyLikes.has(reply.id),
            image_url: reply.image_url || undefined,
            gif_url: reply.gif_url || undefined,
            author: {
              id: reply.public_profiles.id,
              full_name: reply.public_profiles.full_name,
              logo_url: reply.public_profiles.logo_url || ''
            },
            replies: []
          }));

          return {
            id: comment.id,
            content: comment.content,
            created_at: comment.created_at,
            updated_at: comment.updated_at,
            post_id: comment.post_id,
            parent_comment_id: comment.parent_comment_id,
            likes_count: comment.likes_count || 0,
            is_liked: userLikes.has(comment.id),
            image_url: comment.image_url || undefined,
            gif_url: comment.gif_url || undefined,
            author: {
              id: comment.public_profiles.id,
              full_name: comment.public_profiles.full_name,
              logo_url: comment.public_profiles.logo_url || ''
            },
            replies: formattedReplies,
            replies_count: formattedReplies.length
          };
        })
      );

      return commentsWithReplies as Comment[];
    },
    enabled: showComments
  });

  // Add comment mutation
  const addCommentMutation = useMutation({
    mutationFn: async ({ content, image_url, gif_url }: { content: string; image_url?: string; gif_url?: string }) => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('User not authenticated');

      const insertData: any = {
        content,
        post_id: postId,
        author_id: user.id
      };
      if (image_url) insertData.image_url = image_url;
      if (gif_url) insertData.gif_url = gif_url;

      const { data, error } = await supabase
        .from('community_post_comments')
        .insert([insertData])
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      // Invalidar todas as queries de posts e comentários
      queryClient.invalidateQueries({ queryKey: ['comments', postId] });
      queryClient.invalidateQueries({ queryKey: ['posts'] });
      queryClient.invalidateQueries({ queryKey: ['introductions-posts'] });
      queryClient.invalidateQueries({ queryKey: ['learning-posts'] });
      queryClient.invalidateQueries({ queryKey: ['questions-posts'] });
      setNewComment('');
      toast.success('Comentário adicionado com sucesso!');
      toast.success('Comentário adicionado com sucesso!');
    },
    onError: (error) => {
      console.error('Error adding comment:', error);
      toast.error('Erro ao adicionar comentário');
    }
  });

  // Add reply mutation
  const addReplyMutation = useMutation({
    mutationFn: async ({ content, parentCommentId, image_url, gif_url }: { content: string; parentCommentId: string; image_url?: string; gif_url?: string }) => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('User not authenticated');

      const insertData: any = {
        content,
        post_id: postId,
        author_id: user.id,
        parent_comment_id: parentCommentId
      };
      if (image_url) insertData.image_url = image_url;
      if (gif_url) insertData.gif_url = gif_url;

      const { data, error } = await supabase
        .from('community_post_comments')
        .insert([insertData])
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      // Invalidar todas as queries de posts e comentários
      queryClient.invalidateQueries({ queryKey: ['comments', postId] });
      queryClient.invalidateQueries({ queryKey: ['posts'] });
      queryClient.invalidateQueries({ queryKey: ['introductions-posts'] });
      queryClient.invalidateQueries({ queryKey: ['learning-posts'] });
      queryClient.invalidateQueries({ queryKey: ['questions-posts'] });
      
      setReplyContent('');
      setReplyingTo(null);
      toast.success('Resposta adicionada com sucesso!');
      toast.success('Resposta adicionada com sucesso!');
    },
    onError: (error) => {
      console.error('Error adding reply:', error);
      toast.error('Erro ao adicionar resposta');
    }
  });

  const handleAddComment = (image_url?: string, gif_url?: string) => {
    if (newComment.trim() || image_url || gif_url) {
      addCommentMutation.mutate({ content: newComment.trim(), image_url, gif_url });
    }
  };

  const handleAddReply = (parentCommentId: string, image_url?: string, gif_url?: string) => {
    if (replyContent.trim() || image_url || gif_url) {
      addReplyMutation.mutate({ content: replyContent.trim(), parentCommentId, image_url, gif_url });
    }
  };

  return {
    comments,
    isLoading,
    showComments,
    setShowComments,
    newComment,
    setNewComment,
    handleAddComment,
    isAddingComment: addCommentMutation.isPending,
    replyingTo,
    setReplyingTo,
    replyContent,
    setReplyContent,
    handleAddReply,
    isAddingReply: addReplyMutation.isPending
  };
};
